<template>
  <v-dialog
    v-model="dialogVisible"
    max-width="500px"
    persistent
  >
    <v-card class="batch-import-dialog">
      <!-- 标题栏 -->
      <v-card-title class="dialog-header">
        <span class="dialog-title">批量投保</span>
        <v-btn
          icon
          small
          class="close-btn"
          @click="closeDialog"
        >
          <v-icon size="20">close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- 简介说明 -->
      <v-card-text class="dialog-content">
        <div class="intro-section">
          <div class="intro-title">使用说明</div>
          <ul class="intro-list">
            <li>每次只能上传一个 Excel 文件</li>
            <li>请先下载投保文件模板，按模板格式填写被保险人信息</li>
            <li>上传后将显示预览列表，请确认信息无误后提交</li>
          </ul>
          <span class="download-template-link" @click="downloadTemplate">下载投保文件模板</span>
        </div>

        <!-- 上传区域 -->
        <div class="upload-section">
          <div
            class="upload-area"
            :class="{ 'has-file': selectedFile }"
            @click="triggerUpload"
            @dragover.prevent="handleDragOver"
            @dragleave.prevent="handleDragLeave"
            @drop.prevent="handleDrop"
          >
            <input
              ref="fileInput"
              type="file"
              accept=".xlsx,.xls"
              hidden
              @change="handleFileChange"
            >
            <template v-if="!selectedFile">
              <v-icon size="48" color="#ccc">cloud_upload</v-icon>
              <p class="upload-text">点击或拖拽上传 Excel 文件</p>
              <p class="upload-hint">支持 .xlsx, .xls 格式</p>
            </template>
            <template v-else>
              <v-icon size="48" color="#4caf50">insert_drive_file</v-icon>
              <p class="file-name">{{ selectedFile.name }}</p>
              <p class="file-size">{{ formatFileSize(selectedFile.size) }}</p>
              <v-btn
                text
                color="#999"
                small
                class="remove-file-btn"
                @click.stop="removeFile"
              >
                移除
              </v-btn>
            </template>
          </div>
        </div>

        <!-- 错误信息列表 -->
        <div v-if="failList.length > 0" class="fail-section">
          <div class="fail-title">导入失败（{{ failList.length }} 条错误）</div>
          <div class="fail-list">
            <div v-for="(msg, index) in failList" :key="index" class="fail-item">
              <span class="fail-index">{{ index + 1 }}.</span>
              <span class="fail-message">{{ msg.reason }}</span>
            </div>
          </div>
        </div>
      </v-card-text>

      <!-- 底部按钮 -->
      <v-card-actions class="dialog-footer">
        <v-btn
          outlined
          color="#999"
          class="footer-btn"
          @click="closeDialog"
        >
          取消
        </v-btn>
        <v-btn
          color="#dd122a"
          class="footer-btn confirm-btn"
          :disabled="!selectedFile"
          :loading="uploading"
          @click="confirmImport"
        >
          确认导入
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { productUploadFile, importProductData } from '@/api/travelInsurance';

export default {
  name: 'BatchInsuredImport',
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedFile: null,
      previewList: [],
      failList: [], // 存储校验失败的错误信息
      fileUrl: '', // 保存上传后的文件链接
      uploading: false,
      dragOver: false
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },
    hasNonIdCard() {
      return this.previewList.some(item => item.certType && item.certType !== 1);
    }
  },
  watch: {
    value(val) {
      if (!val) {
        this.resetData();
      }
    }
  },
  methods: {
    closeDialog() {
      this.dialogVisible = false;
    },
    resetData() {
      this.selectedFile = null;
      this.previewList = [];
      this.failList = [];
      this.fileUrl = '';
      this.uploading = false;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    triggerUpload() {
      this.$refs.fileInput.click();
    },
    handleFileChange(e) {
      const file = e.target.files[0];
      if (file) {
        this.processFile(file);
      }
    },
    handleDragOver(e) {
      this.dragOver = true;
    },
    handleDragLeave(e) {
      this.dragOver = false;
    },
    handleDrop(e) {
      this.dragOver = false;
      const file = e.dataTransfer.files[0];
      if (file) {
        this.processFile(file);
      }
    },
    processFile(file) {
      // 校验文件类型
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];
      const validExtensions = ['.xlsx', '.xls'];
      const extension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

      if (!validTypes.includes(file.type) && !validExtensions.includes(extension)) {
        this.$message.error('请上传 Excel 文件（.xlsx 或 .xls）');
        return;
      }

      this.selectedFile = file;
      this.parseExcel(file);
    },
    removeFile() {
      this.selectedFile = null;
      this.previewList = [];
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    formatFileSize(size) {
      if (size < 1024) {
        return size + ' B';
      } else if (size < 1024 * 1024) {
        return (size / 1024).toFixed(2) + ' KB';
      } else {
        return (size / (1024 * 1024)).toFixed(2) + ' MB';
      }
    },
    getCertTypeText(certType) {
      const map = {
        1: '身份证', 2: '护照', 3: '军人证', 4: '港澳通行证',
        5: '驾驶证', 6: '港澳居民来往内地通行证', 7: '临时身份证',
        8: '外国人永久居留身份证', 9: '港澳台居民居住证'
      };
      return map[certType] || certType || '-';
    },
    getGenderText(gender) {
      const map = { 0: '男', 1: '女' };
      return map[gender] || gender || '-';
    },
    getRelationTypeText(relationType) {
      const map = {
        1: '本人', 2: '配偶', 3: '父子', 4: '父女', 5: '受益人',
        6: '被保人', 7: '投保人', 8: '转换不详', 9: '其他', 10: '母子',
        11: '母女', 12: '兄弟', 13: '姊妹', 14: '兄妹', 15: '姐弟',
        16: '祖孙', 17: '雇佣', 18: '子女', 19: '父母'
      };
      return map[relationType] || relationType || '-';
    },
    async parseExcel(file) {
      try {
        this.uploading = true;
        // 调用接口上传文件
        const uploadRes = await productUploadFile('excel', file);
        console.log('上传响应:', uploadRes);
        
        // 根据实际返回格式处理
        if (uploadRes && uploadRes.code === 0) {
          this.fileUrl = uploadRes.data; // 保存文件链接
        } else {
          this.$message.error(uploadRes.msg || '上传失败');
        }
      } catch (error) {
        console.error('上传文件失败', error);
        this.$message.error('上传文件失败，请重试');
        this.removeFile();
      } finally {
        this.uploading = false;
      }
    },
    downloadTemplate() {
      const templateUrl = 'https://insurance-broker.cn-wlcb.ufileos.com/individual/template/%E6%89%B9%E9%87%8F%E6%8A%95%E4%BF%9D%E6%A8%A1%E6%9D%BF.xls';
      const link = document.createElement('a');
      link.href = templateUrl;
      link.download = '批量投保模板.xls';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    async confirmImport() {
      if (!this.fileUrl) {
        this.$message.error('请先上传文件');
        return;
      }
      try {
        this.uploading = true;
        // 调用importProductData接口校验数据
        const importRes = await importProductData({ path: this.fileUrl });
        console.log('校验结果:', importRes);
        
        if (importRes && importRes.code === 0) {
          const successList = importRes.data.successList || [];
          const failList = importRes.data.failList || [];
          
          // 有失败数据时展示错误信息，否则展示成功数据
          if (failList.length > 0) {
            this.failList = failList;
            this.previewList = successList;
            this.$message.error(`成功 ${successList.length} 条，失败 ${failList.length} 条`);
          } else {
            this.failList = [];
            this.$message.success(`导入成功，共 ${successList.length} 条`);
            this.$emit('confirm', [...successList], this.fileUrl);
            this.closeDialog();
          }
        } else {
          this.$message.error(importRes.msg || '数据校验失败');
        }
      } catch (error) {
        console.error('导入失败', error);
        this.$message.error('导入失败，请重试');
      } finally {
        this.uploading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.batch-import-dialog {
  border-radius: 8px;

  .dialog-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid #e8e8e8;

    .dialog-title {
      font-size: 16px;
      font-weight: 500;
      color: #333;
    }

    .close-btn {
      margin: 0;
      color: #999;
    }
  }

  .dialog-content {
    padding: 20px;

    .intro-section {
      background-color: #f5f5f5;
      border-radius: 8px;
      padding: 16px;
      margin-bottom: 20px;

      .intro-title {
        font-size: 14px;
        font-weight: 500;
        color: #333;
        margin-bottom: 10px;
      }

      .intro-list {
        margin: 0;
        padding-left: 20px;
        font-size: 13px;
        color: #666;
        line-height: 1.8;

        li {
          margin-bottom: 4px;
        }
      }

      .download-template-link {
        margin-top: 12px;
        padding-left: 20px;
        font-size: 13px;
        color: blue;
        cursor: pointer;
      }
    }

    .upload-section {
      .upload-area {
        border: 2px dashed #ddd;
        border-radius: 8px;
        padding: 40px 20px;
        text-align: center;
        cursor: pointer;
        transition: all 0.3s;

        &:hover {
          border-color: #dd122a;
          background-color: #fff5f5;
        }

        &.has-file {
          border-style: solid;
          border-color: #4caf50;
          background-color: #f0f9f0;
        }

        .upload-text {
          margin-top: 12px;
          font-size: 14px;
          color: #333;
        }

        .upload-hint {
          margin-top: 4px;
          font-size: 12px;
          color: #999;
        }

        .file-name {
          margin-top: 12px;
          font-size: 14px;
          color: #333;
          font-weight: 500;
        }

        .file-size {
          margin-top: 4px;
          font-size: 12px;
          color: #999;
        }

        .remove-file-btn {
          margin-top: 8px;
        }
      }
    }

    .fail-section {
      margin-top: 20px;

      .fail-title {
        font-size: 14px;
        font-weight: 500;
        color: #dd122a;
        margin-bottom: 10px;
      }

      .fail-list {
        max-height: 250px;
        overflow-y: auto;
        background-color: #fff5f5;
        border: 1px solid #f5c6cb;
        border-radius: 8px;
        padding: 12px;

        .fail-item {
          display: flex;
          align-items: flex-start;
          padding: 6px 0;
          font-size: 13px;
          color: #721c24;
          border-bottom: 1px solid #f5c6cb;

          &:last-child {
            border-bottom: none;
          }

          .fail-index {
            margin-right: 8px;
            color: #dd122a;
            font-weight: 500;
          }

          .fail-message {
            flex: 1;
            line-height: 1.5;
          }
        }
      }
    }
  }

  .dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 12px 20px 20px;

    .footer-btn {
      text-transform: none;
      font-size: 14px;
      height: 36px;
      min-width: 80px;
      border-radius: 4px;

      &.confirm-btn {
        color: #fff;
      }
    }
  }
}
</style>
